import {action, computed, makeObservable} from "mobx";
import {
  descriptionMaxLength,
  titleMaxLength
} from "../../../../shared/constants";
import {BooleanObservable} from "../../../observables/booleanObservable";
import {ImageObservable} from "../../../observables/imageObservable";
import {TextObservable} from "../../../observables/textObservable";
import {EventTemplateDuration} from "./eventTemplateDuration";
import {ObservableList} from "../../../observables/observableList";
import {KeyValue} from "../../../observables/keyValue";
import {EventCalendarType} from "../../../../shared/enums/eventCalendarType";
import {hasEnhancedFeatures} from "../../../../shared/eventCalendarTypeHelper";

export default class EventTemplate {
  id: string;
  title: TextObservable;
  description: TextObservable;
  isVillageFavourite: BooleanObservable;
  category: ObservableList<KeyValue>;
  duration: EventTemplateDuration;
  image: ImageObservable;
  calendarType: string;

  constructor(
    id?: string,
    title?: string,
    description?: string,
    isVillageFavourite?: boolean,
    category?: string,
    duration?: number,
    imageFileId?: string,
    calendarType?: string
  ) {
    makeObservable(this, {
      canPublish: computed,
      hasChanges: computed,
      requestObject: computed,
      setIsDirty: action
    });
    this.id = id ?? "";
    this.title = new TextObservable(
      title ?? "",
      titleMaxLength,
      (title) => title.trim() !== ""
    );
    this.description = new TextObservable(
      description ?? "",
      descriptionMaxLength,
      (desc) => desc.trim() !== ""
    );
    this.isVillageFavourite = new BooleanObservable(
      isVillageFavourite ?? false
    );
    this.category = new ObservableList<KeyValue>(
      [],
      category,
      (category) => (category?.value ?? "").trim() !== ""
    );
    this.image = new ImageObservable(
      imageFileId,
      (image) => !hasEnhancedFeatures(this.calendarType) || image !== null
    );
    this.duration = new EventTemplateDuration(
      Math.floor(duration ?? 0),
      Math.round(((duration ?? 0) % 1) * 60)
    );
    this.calendarType = calendarType || EventCalendarType.Enhanced;
  }

  get canPublish() {
    return (
      this.hasChanges &&
      this.title.isValid &&
      this.description.isValid &&
      this.duration.isValid &&
      this.category.isValid &&
      this.image.isValid
    );
  }

  get hasChanges() {
    return (
      this.title.isDirty ||
      this.description.isDirty ||
      this.isVillageFavourite.isDirty ||
      this.category.isDirty ||
      this.image.isDirty ||
      this.duration.hours.isDirty ||
      this.duration.minutes.isDirty
    );
  }

  get requestObject() {
    return {
      id: this.id,
      title: this.title.text.trim(),
      description: this.description.text.trim(),
      isVillageFavourite: this.isVillageFavourite.value,
      category: this.category.selectedItem?.value,
      duration:
        (this.duration.hours.number ?? 0) +
        (this.duration.minutes.number ?? 0) / 60,
      imageFileId: this.image.fileId,
      isActive: true,
      calendarType: this.calendarType
    };
  }

  get durationDescription() {
    if (!this.duration) {
      return "Not set";
    }
    let parts: string[] = [];
    this._buildDescriptionPart(parts, this.duration.hours.number ?? 0, "Hour");
    this._buildDescriptionPart(
      parts,
      this.duration.minutes.number ?? 0,
      "Minute"
    );
    return parts.join(" and ");
  }

  setIsDirty = (value: boolean) => {
    this.title.setIsDirty(value);
    this.description.setIsDirty(value);
    this.isVillageFavourite.setIsDirty(value);
    this.category.setIsDirty(value);
    this.image.setIsDirty(value);
    this.duration.setIsDirty(value);
    this.duration.hours.setIsDirty(value);
    this.duration.minutes.setIsDirty(value);
  };

  private _buildDescriptionPart(
    parts: string[],
    value: number,
    unit: string
  ): void {
    if (value > 0) {
      let description = `${value} ${unit}`;
      if (value > 1) {
        description += "s";
      }
      parts.push(description);
    }
  }
}
